import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play, Volume2, VolumeX, Leaf, Fuel, Check, X, ArrowRight, Flame, Sun, Share2, Link2 } from "lucide-react";
import { useLang } from "../../lib/i18n";
import { useCurrency } from "../../lib/currency";
import { toast } from "sonner";
import { Reveal } from "../motion/Reveal";
import { SectionKicker } from "./Manifesto";
import { VIDEOS, VERSUS_FILM_INDEX, dieselUsdPerHour, dieselAnnualUsd } from "../../lib/data";
import { api } from "../../lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SUFFIX_SYMBOLS = ["CFA", "R", "₵", "KSh"];

const BAR_COUNT = 18;
const randHeights = (min, span) => Array.from({ length: BAR_COUNT }, () => min + Math.random() * span);

const MeterRow = ({ label, db, bars, tone, testid }) => (
  <div className="p-4" data-testid={testid}>
    <div className="flex items-end justify-between mb-2">
      <div className={`flex items-center gap-2 text-sm font-display font-bold ${tone.text}`}>
        <Volume2 size={15} /> {label}
      </div>
      <div className={`font-mono font-extrabold text-xl md:text-2xl tabular-nums ${tone.text}`} data-testid={`${testid}-db`}>
        {db} <span className="text-[11px] font-sans font-medium text-white/40">dB</span>
      </div>
    </div>
    <div className="flex items-end gap-[3px] h-12">
      {bars.map((h, i) => (
        <div
          key={i}
          className={`flex-1 rounded-sm ${tone.bar}`}
          style={{ height: `${h}%`, transition: "height 130ms cubic-bezier(0.22,1,0.36,1)" }}
        />
      ))}
    </div>
  </div>
);

// Live dB "equalizer" — reacts to the video's REAL audio (Web Audio API) once unmuted;
// otherwise simulates: Gesung calm & low, diesel loud & spiky.
const SoundMeter = ({ t, playing, muted, videoRef }) => {
  const m = t.versus.meter;
  const v = t.versus;
  const [dieselBars, setDieselBars] = useState(() => randHeights(8, 14));
  const [gesungBars, setGesungBars] = useState(() => randHeights(6, 8));
  const [dieselDb, setDieselDb] = useState(72);
  const [gesungDb, setGesungDb] = useState(41);
  const [realAudio, setRealAudio] = useState(false);
  const audioRef = useRef({ ctx: null, analyser: null, source: null, raf: null, freq: null });

  const liveReal = playing && !muted;

  // Simulated diesel (idle hum / loud spikes) — only when NOT analysing real audio.
  useEffect(() => {
    if (liveReal) return;
    const id = setInterval(() => {
      if (playing) {
        setDieselBars(randHeights(35, 65));
        setDieselDb(Math.round(88 + Math.random() * 9));
      } else {
        setDieselBars(randHeights(8, 12));
        setDieselDb(Math.round(70 + Math.random() * 5));
      }
    }, 140);
    return () => clearInterval(id);
  }, [liveReal, playing]);

  // Gesung stays calm & gently animated at all times (it's silent no matter what).
  useEffect(() => {
    const id = setInterval(() => {
      setGesungBars(playing ? randHeights(10, 20) : randHeights(6, 8));
      setGesungDb(Math.round((playing ? 43 : 38) + Math.random() * (playing ? 5 : 4)));
    }, 160);
    return () => clearInterval(id);
  }, [playing]);

  // Real-audio analysis → drives the diesel meter from the video's actual sound.
  useEffect(() => {
    if (!liveReal || !videoRef?.current) return;
    const a = audioRef.current;
    try {
      if (!a.ctx) {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        a.ctx = new Ctx();
        a.source = a.ctx.createMediaElementSource(videoRef.current);
        a.analyser = a.ctx.createAnalyser();
        a.analyser.fftSize = 128;
        a.analyser.smoothingTimeConstant = 0.6;
        a.source.connect(a.analyser);
        a.analyser.connect(a.ctx.destination);
        a.freq = new Uint8Array(a.analyser.frequencyBinCount);
      }
      if (a.ctx.state === "suspended") a.ctx.resume().catch(() => {});
    } catch (e) {
      return; // e.g. cross-origin taint — keep simulated fallback
    }
    setRealAudio(true);
    const bins = a.freq;
    const usable = Math.min(bins.length, 48); // skip the very top empty bins
    const step = Math.max(1, Math.floor(usable / BAR_COUNT));
    const loop = () => {
      a.analyser.getByteFrequencyData(bins);
      const bars = [];
      let sum = 0;
      for (let i = 0; i < BAR_COUNT; i++) {
        const val = bins[i * step] || 0;
        bars.push(6 + (val / 255) * 94);
        sum += val;
      }
      const avg = sum / BAR_COUNT; // 0–255
      setDieselBars(bars);
      setDieselDb(avg < 2 ? 40 : Math.round(60 + (avg / 255) * 38)); // ~60–98 dB
      a.raf = requestAnimationFrame(loop);
    };
    a.raf = requestAnimationFrame(loop);
    return () => {
      if (a.raf) cancelAnimationFrame(a.raf);
      setRealAudio(false);
    };
  }, [liveReal, videoRef]);

  const statusText = realAudio ? m.reacting : playing ? m.live : m.idle;

  return (
    <div className="mt-5 rounded-2xl border border-white/10 overflow-hidden" data-testid="sound-meter">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/10">
        <span className="text-[11px] uppercase tracking-[0.22em] text-white/40">{m.heading}</span>
        <span className={`text-[11px] flex items-center gap-1.5 ${playing ? "text-lime2" : "text-white/35"}`} data-testid="sound-meter-status">
          <span className={`w-1.5 h-1.5 rounded-full ${playing ? "bg-lime2 animate-pulse" : "bg-white/30"}`} />
          {statusText}
        </span>
      </div>
      <div className="grid grid-cols-2 divide-x divide-white/10">
        <MeterRow label={v.gesung} db={gesungDb} bars={gesungBars} testid="meter-gesung"
             tone={{ text: "text-lime2", bar: "bg-gradient-to-t from-emerald2/40 to-lime2" }} />
        <MeterRow label={v.diesel} db={dieselDb} bars={dieselBars} testid="meter-diesel"
             tone={{ text: "text-amber-400", bar: "bg-gradient-to-t from-amber-700/50 to-amber-400" }} />
      </div>
    </div>
  );
};

// Live "fuel burning right now" counter — honest real-time diesel spend since page open.
const FuelTicker = ({ t }) => {
  const { cur, format } = useCurrency();
  const tk = t.versus.ticker;
  const perSecUsd = dieselUsdPerHour() / 3600;
  const startRef = useRef(Date.now());
  const [burnedUsd, setBurnedUsd] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setBurnedUsd(((Date.now() - startRef.current) / 1000) * perSecUsd);
    }, 100);
    return () => clearInterval(id);
  }, [perSecUsd]);

  const fmt2 = (usd) => {
    const perSecDisplay = perSecUsd * cur.fx;
    // Choose decimals so the counter visibly ticks in every currency (small-fx like USD need more).
    const decimals = perSecDisplay >= 1 ? 0 : perSecDisplay >= 0.1 ? 2 : 3;
    const num = (usd * cur.fx).toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
    return SUFFIX_SYMBOLS.includes(cur.symbol) ? `${num} ${cur.symbol}` : `${cur.symbol}${num}`;
  };

  return (
    <div className="mt-5 rounded-2xl border border-white/10 overflow-hidden" data-testid="fuel-ticker">
      <div className="px-4 py-2.5 text-[11px] uppercase tracking-[0.22em] text-white/40 border-b border-white/10">
        {tk.heading}
      </div>
      <div className="grid grid-cols-2 divide-x divide-white/10">
        {/* Diesel — burning */}
        <div className="p-4 bg-amber-500/[0.07]">
          <div className="flex items-center gap-2 text-amber-300 text-sm font-display font-bold">
            <Flame size={15} className="animate-pulse" /> {tk.diesel}
          </div>
          <div
            className="mt-2 font-mono font-extrabold text-2xl md:text-3xl text-amber-400 tabular-nums tracking-tight"
            data-testid="fuel-ticker-diesel"
          >
            {fmt2(burnedUsd)}
          </div>
          <p className="mt-1 text-[11px] text-white/45 leading-snug">{tk.burned}</p>
        </div>
        {/* Gesung — zero */}
        <div className="p-4 bg-emerald2/[0.08]">
          <div className="flex items-center gap-2 text-lime2 text-sm font-display font-bold">
            <Sun size={15} /> {tk.gesung}
          </div>
          <div className="mt-2 font-mono font-extrabold text-2xl md:text-3xl text-lime2 tabular-nums tracking-tight" data-testid="fuel-ticker-gesung">
            {fmt2(0)}
          </div>
          <p className="mt-1 text-[11px] text-white/45 leading-snug">{tk.fuelFree}</p>
        </div>
      </div>
      <div className="px-4 py-2.5 text-[12px] text-white/55 border-t border-white/10" data-testid="fuel-ticker-annual">
        {tk.yearPre} <span className="text-amber-300 font-semibold">{format(dieselAnnualUsd())}</span> {tk.yearSuf}
      </div>
    </div>
  );
};

export const SilentVsDiesel = () => {
  const { t } = useLang();
  const { format } = useCurrency();
  const v = t.versus;
  const videoRef = useRef(null);
  const [film, setFilm] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);

  const shareComparison = () => {
    const savings = format(dieselAnnualUsd());
    const url = `${window.location.origin}/#versus`;
    const text = `${v.shareMsg.replace("{savings}", savings)} ${url}`;
    if (navigator.share) {
      navigator.share({ title: v.title, text, url }).catch(() => {});
      return;
    }
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener,noreferrer");
  };

  const copyLink = async () => {
    const url = `${window.location.origin}/#versus`;
    try {
      await navigator.clipboard.writeText(url);
    } catch (e) {
      const ta = document.createElement("textarea");
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
    }
    toast.success(v.linkCopied);
  };

  useEffect(() => {
    api
      .get("/films")
      .then((r) => setFilm((r.data || []).find((f) => f.index === VERSUS_FILM_INDEX) || null))
      .catch(() => {});
  }, []);

  const fallback = VIDEOS[VERSUS_FILM_INDEX] || {};
  const src = film && film.has_upload ? `${BACKEND_URL}${film.url}` : fallback.mp4;
  const poster = film && film.has_poster ? `${BACKEND_URL}${film.poster_url}` : fallback.poster;

  const start = () => {
    const el = videoRef.current;
    if (!el) return;
    setPlaying(true);
    el.muted = muted;
    el.play().catch(() => {});
  };

  const toggleMute = (e) => {
    e.stopPropagation();
    const el = videoRef.current;
    if (!el) return;
    el.muted = !el.muted;
    setMuted(el.muted);
  };

  return (
    <section
      id="versus"
      className="relative z-10 max-w-[1400px] mx-auto px-5 md:px-10 py-24 md:py-32"
      data-testid="versus-section"
    >
      <Reveal>
        <SectionKicker>{v.kicker}</SectionKicker>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <h2 className="font-display font-extrabold tracking-tighter text-4xl md:text-6xl max-w-2xl leading-[0.95]">
            {v.title}
          </h2>
          <p className="text-white/60 max-w-sm text-sm md:text-base">{v.sub}</p>
        </div>
      </Reveal>

      <div className="mt-14 grid lg:grid-cols-5 gap-6 lg:gap-8 items-start">
        {/* Video */}
        <Reveal className="lg:col-span-3">
          <div
            className="relative aspect-video rounded-2xl overflow-hidden border border-white/10 bg-black group"
            data-testid="versus-video"
          >
            <video
              ref={videoRef}
              poster={poster}
              controls={playing}
              loop
              playsInline
              preload="none"
              className="absolute inset-0 w-full h-full object-cover"
            >
              <source src={src} type="video/mp4" />
            </video>

            {!playing && (
              <button
                onClick={start}
                data-testid="versus-play-btn"
                className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-gradient-to-t from-black/80 via-black/30 to-black/40 transition-colors"
              >
                <span className="grid place-items-center w-20 h-20 rounded-full bg-white/10 backdrop-blur-md border border-white/30 group-hover:bg-emerald2 group-hover:border-emerald2 transition-colors duration-300">
                  <Play size={26} className="ml-1 text-white" />
                </span>
                <span className="text-xs uppercase tracking-[0.25em] text-white/80">{v.playCta}</span>
              </button>
            )}

            {playing && (
              <button
                onClick={toggleMute}
                data-testid="versus-mute-btn"
                className="absolute top-4 right-4 z-10 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/80 hover:text-lime2 border border-white/20 hover:border-lime2 bg-black/50 rounded-full px-3 py-1.5 transition-colors"
              >
                {muted ? <><VolumeX size={14} /> Sound</> : <><Volume2 size={14} /> On</>}
              </button>
            )}

            {!(film && film.has_upload) && (
              <span
                className="absolute bottom-4 left-4 z-10 text-[11px] text-white/70 bg-black/55 border border-white/15 rounded-full px-3 py-1"
                data-testid="versus-soon-note"
              >
                {v.soon}
              </span>
            )}
          </div>
          <SoundMeter t={t} playing={playing} muted={muted} videoRef={videoRef} />
          <FuelTicker t={t} />
        </Reveal>

        {/* Comparison panel */}
        <Reveal delay={0.1} className="lg:col-span-2">
          <div className="rounded-2xl border border-white/10 overflow-hidden" data-testid="versus-panel">
            <div className="grid grid-cols-2 text-sm font-display font-bold">
              <div className="flex items-center gap-2 px-4 py-3 bg-emerald2/15 text-lime2 border-b border-white/10">
                <Leaf size={15} /> {v.gesung}
              </div>
              <div className="flex items-center gap-2 px-4 py-3 bg-amber-500/10 text-amber-300 border-b border-l border-white/10">
                <Fuel size={15} /> {v.diesel}
              </div>
            </div>
            {v.rows.map((row, i) => (
              <div key={i} className="border-b border-white/10 last:border-b-0" data-testid={`versus-row-${i}`}>
                <div className="px-4 pt-3 text-[11px] uppercase tracking-[0.2em] text-white/40">{row.label}</div>
                <div className="grid grid-cols-2">
                  <div className="flex items-start gap-2 px-4 py-2.5 text-white/80 text-sm">
                    <Check size={15} className="text-lime2 shrink-0 mt-0.5" />
                    <span>{row.g}</span>
                  </div>
                  <div className="flex items-start gap-2 px-4 py-2.5 border-l border-white/10 text-white/50 text-sm">
                    <X size={15} className="text-amber-400/80 shrink-0 mt-0.5" />
                    <span>{row.d}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <a
            href="#contact"
            data-testid="versus-quote-cta"
            className="mt-5 inline-flex items-center justify-center gap-2 w-full rounded-full bg-lime2 text-charcoal font-semibold h-12 hover:bg-white transition-colors"
          >
            {v.quoteCta} <ArrowRight size={16} />
          </a>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={shareComparison}
              data-testid="versus-share-btn"
              className="inline-flex items-center justify-center gap-2 w-full rounded-full border border-lime2/40 text-lime2 hover:bg-lime2/10 font-semibold h-12 transition-colors"
            >
              <Share2 size={16} /> {v.share}
            </button>
            <button
              type="button"
              onClick={copyLink}
              data-testid="versus-copy-link-btn"
              className="inline-flex items-center justify-center gap-2 w-full rounded-full border border-white/15 text-white/70 hover:border-white/40 hover:text-white font-semibold h-12 transition-colors"
            >
              <Link2 size={16} /> {v.copyLink}
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
